import { useMemo } from 'react';
import { isTimeWindowEqual, type TimeWindow } from './utils';

type TimeWindowKey = TimeWindow | undefined;

/**
 * A specialized Map class for storing and retrieving DOM element references
 * based on a `timeWindow` object.
 */
export class RefRegistry extends Map<TimeWindowKey, HTMLElement> {
    /**
     * A helper method designed to be used as a React callback ref.
     * It sets or deletes an entry in the Map based on the ref's presence.
     */
    setTimeWindow =
        (timeWindow: TimeWindow): ((ref: HTMLElement | null) => void) =>
        (ref) => {
            if (ref) {
                // Store the 'source' as the fed timeWindow can be one produced by `extendTemplate`.
                this.set(timeWindow.sourceTimeWindow, ref);
            } else {
                // When the component unmounts, the ref callback is called with `null`.
                // We can use this to clean up the Map and prevent memory leaks.
                this.delete(timeWindow.sourceTimeWindow);
            }
        };

    /**
     * Finds a ref in the registry by comparing the properties of a timeWindow object,
     * rather than by object reference.
     */
    findRefByTimeWindow = (timeWindow: TimeWindow): HTMLElement | null => {
        const foundEntry = Array.from(this.entries()).find(
            ([key]) => key && isTimeWindowEqual(key, timeWindow)
        );

        return foundEntry ? foundEntry[1] : null;
    };

    static use(): RefRegistry {
        // We do not use class components, warning below can be ignored.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useMemo(() => new this(), []);
    }
}
